import {
    Params,
} from 'react-router-dom'
import {
    RequestConfig,
    RequestConfigFunction,
} from 'app/types/request.types'
import queryClient, {
    invalidateQueries,
} from 'app/queryClient'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import simpleApi from 'app/services/simpleApi'
import sleep from 'app/utils/sleep'

/**
 * Defines the parameters for the skyNetQueryLoader as a single, combined type.
 */
export type SkyNetQueryLoaderParams = {
    queryFn: (args: { routeParams: Params, token: string }) => Promise<any>;
    requestKey: RequestKeys;
    convert?: (data: any) => any;
    onError?: (error: unknown) => void;
    staleTime?: number;
    params?: never;
    requestConfigFunction?: never;
    adapter?: never;
} | {
    queryFn?: never;
    requestKey: RequestKeys;
    requestConfigFunction: RequestConfigFunction;
    params?: Record<string, any>;
    adapter?: <P = { id: number }>(
        requestConfigFunction: RequestConfigFunction<P>
    ) => (params: Params) => RequestConfig
    convert?: (data: Record<string, any>) => Record<string, any>;
    onError?: (error: unknown) => void;
    staleTime?: number;
};

export type SkyNetLoaderInitFunction =
    <ResponseObject = {
        data: any,
        invalidate: () => void
    }>(config: SkyNetQueryLoaderParams) => (args: {
        params?: Params
    }) => Promise<ResponseObject>

const defaultRouteParamsToRequestAdapter = (
    requestConfigFunction,
) => {
    return (({
        key,
    }: { key: string }) => {
        return requestConfigFunction({
            id: Number(key),
        })
    })
}

export default function skyNetQueryLoader(token: string) {
    return ({
        requestConfigFunction,
        params,
        queryFn,
        requestKey,
        staleTime = 10000,
        convert,
        onError,
        adapter = defaultRouteParamsToRequestAdapter,
    }: SkyNetQueryLoaderParams) => {
        return async ({
            params: routeParams,
        }) => {
            try {
                const data = await queryClient.fetchQuery({
                    queryKey: [
                        requestKey,
                        {
                            ...routeParams,
                        },
                    ],
                    queryFn: queryFn ? (() => {
                        return queryFn({
                            routeParams,
                            token,
                        })
                    }) : (() => {
                        return simpleApi({
                            ...adapter(requestConfigFunction)(routeParams),
                            data: params,
                            token,
                        })
                    }),
                    staleTime,
                })

                /* Requests run too quickly; need to slow them for useFetch to work properly */
                // TODO-JM: implement condition delay IP-22300
                await sleep(100)

                return {
                    data: convert?.(data) ?? data,
                    invalidate: () => {
                        return invalidateQueries([requestKey])
                    },
                }
            } catch (e) {
                onError?.(e)
                return null
            }
        }
    }
}
